import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateQuizQuestionMutation } from "../../slices/quizQuestionApiSlice";

const QuizQuestionCreateScreen = () => {
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState("");
  const [question, setQuestion] = useState("");
  const [order, setOrder] = useState(1);
  const [marks, setMarks] = useState(1);

  const [createQuizQuestion, { isLoading }] =
    useCreateQuizQuestionMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!quiz.trim()) {
      toast.error("Quiz is required");
      return;
    }

    if (!question.trim()) {
      toast.error("Question is required");
      return;
    }

    try {
      await createQuizQuestion({
        quiz,
        question,
        order: Number(order),
        marks: Number(marks),
      }).unwrap();

      toast.success("Question added to quiz successfully");

      navigate("/quiz-questions");
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to add question to quiz"
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/quiz-questions"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-white shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Quiz Questions
        </Link>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Add Question to Quiz
            </h1>

            <p className="mt-2 text-gray-500">
              Map a question to a quiz and configure its order and marks.
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Adding question...
            </div>
          )}

          <form onSubmit={submitHandler} className="space-y-6">
            {/* Quiz */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Quiz ID <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={quiz}
                onChange={(e) => setQuiz(e.target.value)}
                placeholder="Enter Quiz ID"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Question */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Question ID <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Enter Question ID"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Order */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Display Order
              </label>

              <input
                type="number"
                min="1"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Marks */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Marks
              </label>

              <input
                type="number"
                min="1"
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-60"
              >
                {isLoading ? "Adding..." : "Add Question"}
              </button>

              <Link
                to="/quiz-questions"
                className="flex-1 rounded-xl border border-gray-300 bg-white py-3.5 text-center text-lg font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuizQuestionCreateScreen;